// src/utils/workflow.js
import { userNorm } from "../utils/norms";
import { formatTimestampWithTz, writeWorkflowEntry } from "../services/api";
import { cn } from "./norms"

export const WF = {
  TO_REASSIGN: "To be reassigned",
  DRAFT_NEEDS_REVIEW: "Draft change needs review",
  DRAFT_ACCEPTED: "Draft change accepted",
  DRAFT_REJECTED: "Draft change rejected",
  ELECTIVE: "Elective reassignment",
  EXCEL_IMPORT: "Excel Import Reassignment"
};

// Simple guard: only allow legal transitions
// export const canTransition = (from, to) => {
//   switch (from) {
//     case WF.TO_REASSIGN:
//       return to === WF.DRAFT_NEEDS_REVIEW;
//     case WF.DRAFT_NEEDS_REVIEW:
//       return to === WF.DRAFT_ACCEPTED || to === WF.DRAFT_REJECTED;
//     default:
//       return false;
//   }
// };

// Allowed transitions
const TRANSITIONS = {
  [WF.TO_REASSIGN]:   new Set([WF.DRAFT_NEEDS_REVIEW]),
  [WF.DRAFT_NEEDS_REVIEW]: new Set([WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED]),
  [WF.DRAFT_ACCEPTED]:     new Set([WF.ELECTIVE]),   // re-open via bulk modal
  [WF.DRAFT_REJECTED]:     new Set([WF.DRAFT_NEEDS_REVIEW, WF.ELECTIVE]),   // re-open via bulk modal, or loop back
  [WF.EXCEL_IMPORT]:        new Set([WF.DRAFT_NEEDS_REVIEW])
};

// Admin can fast-track directly from TO_REASSIGN
const TRANSITIONS_ADMIN = {
  [WF.TO_REASSIGN]:         new Set([WF.DRAFT_NEEDS_REVIEW, WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED]),
  [WF.DRAFT_NEEDS_REVIEW]:  new Set([WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED]),
  [WF.DRAFT_ACCEPTED]:      new Set([WF.ELECTIVE]),
  [WF.DRAFT_REJECTED]:      new Set([WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED, WF.ELECTIVE]),
  [WF.ELECTIVE]:            new Set([WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED]),
  [WF.EXCEL_IMPORT]:        new Set([WF.DRAFT_ACCEPTED, WF.DRAFT_REJECTED])
};

// Statuses whose staff pick is still editable, so the Excel import may fill it in
// place instead of refusing the row. Mirrors the `locked` rule in DraftsStep: once a
// task is with the committee (needs review) or accepted, its pick stands.
export const IMPORT_OVERWRITABLE = new Set([
  WF.TO_REASSIGN,
  WF.ELECTIVE,
  WF.DRAFT_REJECTED,
  WF.EXCEL_IMPORT,
]);

// export const canTransition = (from, to) => TRANSITIONS[from]?.has(to) ?? false;

// Accept an options bag so we can flip to the admin table
export const canTransition = (from, to, { admin = false } = {}) =>
  (admin ? TRANSITIONS_ADMIN : TRANSITIONS)[from]?.has(to) ?? false;

export const serviceForTask = (task) =>
  (task.role === "SP" || task.role === "SM") ? (task.service || "NA") : "NA";

// Keyify building from individual parts (when you don't have task object)
const keyifyFromParts = (clientCode, role, service, username) => {
  const up = (s="") => String(s).trim().toUpperCase();
  return `${up(clientCode)}_${up(role)}_${up(service || "NA")}_${userNorm(username)}`;
};

// Keyify from single task object
const keyifyFromTask = (task) => {
  const up = (s="") => String(s).trim().toUpperCase();
  const service = serviceForTask(task);
  const currentUsername = userNorm(task.currentUsername || task.userFor || task.USER_FOR_REASSIGNMENT || "unknownuser");
  const key = `${up(task.clientCode)}_${up(task.role)}_${up(service || "NA")}_${currentUsername}`;
  return key
}

// consolidated keyify
export const keyify = (...args) => {
  if (args.length === 1 && typeof args[0] === "object") return keyifyFromTask(args[0]);
  const [clientCode, role, service, username] = args;
  return keyifyFromParts(clientCode, role, service, username);
};

export function buildEntry({
  task, newStaffIdx, empByIndex,
  fromUser, toUser, status
}) {

  const pickedIdx =
   newStaffIdx != null && Number.isFinite(Number(newStaffIdx))
      ? Number(newStaffIdx)
      : null;

  const service = serviceForTask(task);
  const currentUsername = userNorm(task.currentUsername || task.userFor || task.USER_FOR_REASSIGNMENT || "unknownuser");
  const KEY = keyify(task);

  const newStaffName = pickedIdx
    ? cn(empByIndex.get(pickedIdx), "staffname", "")
    : (task.wfPendingStaff || "");  // carry over if admin is deciding

  return {
    CONTINDEX: task.contIndex,
    CLIENTCODE: task.clientCode,
    FROM_USER: userNorm(fromUser || ""),
    KEY,
    ROLE_FOR_REASSIGNMENT: task.role,
    SERVICE: service,
    TO_USER: toUser ? userNorm(toUser) : null,
    UPDATED_TS: formatTimestampWithTz(new Date()),
    USER_FOR_REASSIGNMENT: currentUsername,
    NEW_STAFF_SELECTION: newStaffName,
    NEW_STAFF_SELECTION_IDX: pickedIdx,
    WORKFLOW: status,
  };
}

/**
 * Master updater — everyone calls this.
 *
 * intents:
 *  - 'seed'           : create initial metadata for missing tasks
 *  - 'owner_submit'   : partner submits draft -> needs review
 *  - 'admin_accept'   : committee accepts draft
 *  - 'admin_reject'   : committee rejects draft
 */
export async function updateWorkflow({
  intent,
  task,
  newStaffIdx,      // required for owner_submit; ignored for others
  actor,            // logged-in username (owner or admin)
  routeTo,          // override TO_USER if you need; optional
  reviewer,         // admin reviewer username for accept/reject
  empByIndex,
  detail,
  isAdmin = false,
  write = writeWorkflowEntry,     // DI for tests
}) {
  const actorU = userNorm(actor || "");
  const reviewerU = userNorm(reviewer || "");
  // const service = serviceForTask(task);

  // Choose status/ownership by intent
  let status, fromUser, toUser;
  if (intent === "seed") {
    status   = WF.TO_REASSIGN;
    fromUser = "sysadmin";
    toUser   = routeTo || "sysadmin";           // you set originator/CP in the caller
  }
  else if (intent === "elective") {
    status   = WF.ELECTIVE;
    fromUser = "sysadmin";
    toUser   = routeTo || "sysadmin";
  }
  else if (intent === "excel_import") {
    status   = WF.EXCEL_IMPORT;
    fromUser = actorU || "sysadmin";
    toUser   = routeTo || "sysadmin";
  }
  else if (intent === "owner_submit") {
    status   = WF.DRAFT_NEEDS_REVIEW;
    fromUser = actorU;
    toUser   = routeTo || "sysadmin";           // committee
  }
  else if (intent === "admin_accept") {
    status   = WF.DRAFT_ACCEPTED;
    fromUser = reviewerU;
    toUser   = reviewerU;                       // or keep "sysadmin"
  }
  else if (intent === "admin_reject") {
    status   = WF.DRAFT_REJECTED;
    fromUser = reviewerU;
    toUser   = routeTo || reviewerU;
  } else {
    throw new Error(`Unknown intent: ${intent}`);
  }

  // Validate transition if the task already has a status
  // if (task.wfStatus && !canTransition(task.wfStatus, status)) {
  //   throw new Error(`Illegal transition ${task.wfStatus} → ${status}`);
  // }

  // Admin intents may bypass the owner-only restriction
  const adminIntent = intent === "admin_accept" || intent === "admin_reject";
  const skipTransitionCheck = intent === "excel_import";
  if (!skipTransitionCheck && task.wfStatus && !canTransition(task.wfStatus, status, { admin: isAdmin || adminIntent })) {
    throw new Error(`Illegal transition ${task.wfStatus} → ${status}`);
  }

  const entry = buildEntry({ task, newStaffIdx, empByIndex, fromUser, toUser, status });
  await write(entry);
  return entry;
}